import { trackEvent } from "@/utils/analytics";
import logger from "@/utils/logger";

/**
 * Analytics Dashboard Utility
 * Provides comprehensive metrics and reporting for checkout conversion analysis
 */

// Metric categories for dashboard reporting
export const METRIC_CATEGORIES = {
  CONVERSION_RATES: "conversion_rates",
  PERFORMANCE_TIMING: "performance_timing", 
  ERROR_ANALYSIS: "error_analysis",
  USER_BEHAVIOR: "user_behavior",
  REVENUE_TRACKING: "revenue_tracking",
};

// Time periods for analytics aggregation
export const TIME_PERIODS = {
  HOURLY: "hourly",
  DAILY: "daily", 
  WEEKLY: "weekly",
  MONTHLY: "monthly",
};

/**
 * Track conversion rate metrics with detailed categorization
 * @param {string} metricType - Type of conversion metric
 * @param {Object} conversionData - Conversion data
 */
export const trackConversionRate = (metricType, conversionData = {}) => {
  const eventData = {
    metric_category: METRIC_CATEGORIES.CONVERSION_RATES,
    metric_type: metricType,
    timestamp: new Date().toISOString(),
    ...conversionData,
  };

  logger.log(`Conversion rate tracked: ${metricType}`, eventData);
  return trackEvent("conversion_rate_metric", eventData);
};

/**
 * Track payment success rate with error categorization
 * @param {boolean} success - Whether payment was successful
 * @param {Object} paymentData - Payment details
 */
export const trackPaymentSuccessRate = (success, paymentData = {}) => {
  const eventData = {
    success: success,
    payment_method: paymentData.paymentMethod || "stripe",
    pricing_tier: paymentData.pricingTier,
    error_category: success ? null : paymentData.errorCategory,
    error_code: success ? null : paymentData.errorCode,
    retry_attempt: paymentData.retryAttempt || 0,
    ...paymentData,
  };

  return trackConversionRate("payment_success_rate", eventData);
};

/**
 * Track account creation success rate with failure analysis
 * @param {boolean} success - Whether account creation was successful
 * @param {Object} accountData - Account creation details
 */
export const trackAccountCreationRate = (success, accountData = {}) => {
  const eventData = {
    success: success,
    twilio_integration: accountData.twilioIntegration || false,
    email_service: accountData.emailService || "sendgrid",
    error_category: success ? null : accountData.errorCategory,
    setup_time_ms: accountData.setupTimeMs,
    retry_attempt: accountData.retryAttempt || 0,
    ...accountData,
  };

  return trackConversionRate("account_creation_success_rate", eventData);
};

/**
 * Track overall checkout conversion rate
 * @param {boolean} success - Whether entire checkout flow was successful
 * @param {Object} checkoutData - Complete checkout flow data
 */
export const trackOverallConversionRate = (success, checkoutData = {}) => {
  const eventData = {
    success: success,
    total_time_ms: checkoutData.totalTimeMs,
    steps_completed: checkoutData.stepsCompleted || 0,
    total_steps: checkoutData.totalSteps || 10,
    abandonment_point: success ? null : checkoutData.abandonmentPoint,
    pricing_tier: checkoutData.pricingTier,
    user_agent: navigator.userAgent,
    referrer: document.referrer,
    ...checkoutData,
  };

  return trackConversionRate("overall_conversion_rate", eventData);
};

/**
 * Track performance timing metrics for optimization
 * @param {string} timingType - Type of timing measurement
 * @param {number} duration - Duration in milliseconds
 * @param {Object} timingData - Additional timing context
 */
export const trackPerformanceTiming = (timingType, duration, timingData = {}) => {
  const eventData = {
    metric_category: METRIC_CATEGORIES.PERFORMANCE_TIMING,
    timing_type: timingType,
    duration_ms: duration,
    duration_seconds: Math.round(duration / 1000),
    duration_minutes: Math.round(duration / 60000),
    timestamp: new Date().toISOString(),
    ...timingData,
  };

  logger.log(`Performance timing tracked: ${timingType}`, eventData);
  return trackEvent("performance_timing_metric", eventData);
};

/**
 * Track time to complete checkout form
 * @param {number} duration - Time in milliseconds
 * @param {Object} formData - Form completion context
 */
export const trackFormCompletionTime = (duration, formData = {}) => {
  return trackPerformanceTiming("form_completion", duration, {
    form_fields_count: formData.fieldsCount || 2,
    validation_errors: formData.validationErrors || 0,
    field_focus_count: formData.fieldFocusCount || 0,
    ...formData,
  });
};

/**
 * Track time to complete payment processing
 * @param {number} duration - Time in milliseconds
 * @param {Object} paymentData - Payment processing context
 */
export const trackPaymentProcessingTime = (duration, paymentData = {}) => {
  return trackPerformanceTiming("payment_processing", duration, {
    payment_method: paymentData.paymentMethod || "stripe",
    pricing_tier: paymentData.pricingTier,
    redirect_time: paymentData.redirectTime,
    ...paymentData,
  });
};

/**
 * Track time to complete account setup
 * @param {number} duration - Time in milliseconds
 * @param {Object} setupData - Account setup context
 */
export const trackAccountSetupTime = (duration, setupData = {}) => {
  return trackPerformanceTiming("account_setup", duration, {
    twilio_setup_time: setupData.twilioSetupTime,
    email_send_time: setupData.emailSendTime,
    database_write_time: setupData.databaseWriteTime,
    ...setupData,
  });
};

/**
 * Track complete checkout flow time
 * @param {number} duration - Total time in milliseconds
 * @param {Object} flowData - Complete flow context
 */
export const trackCompleteFlowTime = (duration, flowData = {}) => {
  return trackPerformanceTiming("complete_checkout_flow", duration, {
    steps_completed: flowData.stepsCompleted,
    error_count: flowData.errorCount || 0,
    retry_count: flowData.retryCount || 0,
    pricing_tier: flowData.pricingTier,
    ...flowData,
  });
};

/**
 * Track error analysis metrics for debugging and optimization
 * @param {string} errorType - Type of error
 * @param {Object} errorData - Error details and context
 */
export const trackErrorAnalysis = (errorType, errorData = {}) => {
  const eventData = {
    metric_category: METRIC_CATEGORIES.ERROR_ANALYSIS,
    error_type: errorType,
    error_frequency: errorData.frequency || 1,
    error_impact: errorData.impact || "medium", // low, medium, high
    resolution_time: errorData.resolutionTime,
    user_recovered: errorData.userRecovered || false,
    timestamp: new Date().toISOString(),
    ...errorData,
  };

  logger.error(`Error analysis tracked: ${errorType}`, eventData);
  return trackEvent("error_analysis_metric", eventData);
};

/**
 * Track user behavior patterns during checkout
 * @param {string} behaviorType - Type of behavior
 * @param {Object} behaviorData - Behavior details
 */
export const trackUserBehavior = (behaviorType, behaviorData = {}) => {
  const eventData = {
    metric_category: METRIC_CATEGORIES.USER_BEHAVIOR,
    behavior_type: behaviorType,
    session_duration: behaviorData.sessionDuration,
    interaction_count: behaviorData.interactionCount || 1,
    device_type: getDeviceType(),
    browser_type: getBrowserType(),
    timestamp: new Date().toISOString(),
    ...behaviorData,
  };

  return trackEvent("user_behavior_metric", eventData);
};

/**
 * Track checkout abandonment patterns
 * @param {string} abandonmentPoint - Where user abandoned
 * @param {Object} abandonmentData - Abandonment context
 */
export const trackCheckoutAbandonment = (abandonmentPoint, abandonmentData = {}) => {
  return trackUserBehavior("checkout_abandonment", {
    abandonment_point: abandonmentPoint,
    time_spent: abandonmentData.timeSpent,
    steps_completed: abandonmentData.stepsCompleted,
    last_interaction: abandonmentData.lastInteraction,
    pricing_tier: abandonmentData.pricingTier,
    ...abandonmentData,
  });
};

/**
 * Track form interaction patterns
 * @param {Object} interactionData - Form interaction details
 */
export const trackFormInteractionPattern = (interactionData = {}) => {
  return trackUserBehavior("form_interaction_pattern", {
    field_completion_order: interactionData.fieldCompletionOrder,
    validation_error_count: interactionData.validationErrorCount || 0,
    field_focus_time: interactionData.fieldFocusTime,
    copy_paste_usage: interactionData.copyPasteUsage || false,
    auto_fill_usage: interactionData.autoFillUsage || false,
    ...interactionData,
  });
};

/**
 * Track revenue metrics for business analysis
 * @param {string} revenueType - Type of revenue metric
 * @param {Object} revenueData - Revenue details
 */
export const trackRevenueMetric = (revenueType, revenueData = {}) => {
  const eventData = {
    metric_category: METRIC_CATEGORIES.REVENUE_TRACKING,
    revenue_type: revenueType,
    amount: revenueData.amount,
    currency: revenueData.currency || "AUD",
    pricing_tier: revenueData.pricingTier,
    customer_lifetime_value: revenueData.customerLifetimeValue,
    acquisition_cost: revenueData.acquisitionCost,
    timestamp: new Date().toISOString(),
    ...revenueData,
  };

  return trackEvent("revenue_metric", eventData);
};

/**
 * Generate comprehensive analytics report
 * @param {Object} reportData - Report parameters
 * @returns {Object} Analytics report summary
 */
export const generateAnalyticsReport = (reportData = {}) => {
  const report = {
    reportId: `analytics_${Date.now()}`,
    generatedAt: new Date().toISOString(),
    timePeriod: reportData.timePeriod || TIME_PERIODS.DAILY,
    metrics: {
      conversionRates: reportData.conversionRates || {},
      performanceTiming: reportData.performanceTiming || {},
      errorAnalysis: reportData.errorAnalysis || {},
      userBehavior: reportData.userBehavior || {},
      revenueTracking: reportData.revenueTracking || {},
    },
    recommendations: generateRecommendations(reportData),
  };

  // Track report generation
  trackEvent("analytics_report_generated", {
    report_id: report.reportId,
    time_period: report.timePeriod,
    metrics_included: Object.keys(report.metrics).length,
  });

  return report;
};

/**
 * Generate optimization recommendations based on analytics data
 * @param {Object} analyticsData - Analytics data for analysis
 * @returns {Array} Array of recommendations
 */
const generateRecommendations = (analyticsData) => {
  const recommendations = [];

  // Analyze conversion rates
  if (analyticsData.paymentSuccessRate < 0.85) {
    recommendations.push({
      type: "conversion_optimization",
      priority: "high",
      message: "Payment success rate is below 85%. Consider improving error handling and retry mechanisms.",
    });
  }

  // Analyze performance timing
  if (analyticsData.averageCheckoutTime > 300000) { // 5 minutes
    recommendations.push({
      type: "performance_optimization", 
      priority: "medium",
      message: "Average checkout time exceeds 5 minutes. Consider streamlining the checkout process.",
    });
  }

  // Analyze error patterns
  if (analyticsData.errorRate > 0.1) { // 10%
    recommendations.push({
      type: "error_reduction",
      priority: "high", 
      message: "Error rate is above 10%. Focus on improving form validation and error recovery.",
    });
  }

  return recommendations;
};

/**
 * Utility functions for device and browser detection
 */
const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return "tablet";
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return "mobile";
  }
  return "desktop";
};

const getBrowserType = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes("Chrome")) return "chrome";
  if (userAgent.includes("Firefox")) return "firefox";
  if (userAgent.includes("Safari")) return "safari";
  if (userAgent.includes("Edge")) return "edge";
  return "other";
};